import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import { OtpInput } from '@/components/auth/OtpInput';
import { useAuth } from '@/app/AuthProvider';
import {
  pickPrimaryPhoneFactor,
  useChallengePhoneFactor,
  useVerifyChallenge,
} from '@/features/auth/mfa';

interface NavState {
  from?: { pathname: string };
}

/**
 * Where users land after sign-in if they already have a verified phone
 * factor but the current session is still AAL1. Reachable from RequireMfa
 * which redirects with `state.from` so we can return them to the page
 * they were trying to view (e.g. /financials/transactions).
 */
export function MfaChallengePage() {
  const { mfa, profile, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as NavState | null) ?? null;

  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [issuing, setIssuing] = useState(false);
  const issuedRef = useRef(false);

  const factor = pickPrimaryPhoneFactor(mfa.factors);

  const challengeMut = useChallengePhoneFactor();
  const verifyMut = useVerifyChallenge();

  // Auto-issue the first SMS the moment we land here. Keep this in a
  // separate effect (not folded into render) so it runs exactly once
  // even under React strict mode double-mounting in dev.
  useEffect(() => {
    if (loading) return;
    if (!factor) return;
    if (issuedRef.current) return;
    issuedRef.current = true;
    setIssuing(true);
    const phoneHint = factor.friendly_name;
    challengeMut
      .mutateAsync({ factorId: factor.id })
      .then((res) => {
        setChallengeId(res.challengeId);
        toast.success(
          phoneHint
            ? `Code sent to ${maskPhone(phoneHint)}.`
            : 'Verification code sent.',
        );
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : 'Could not send the code.';
        toast.error(msg);
      })
      .finally(() => setIssuing(false));
  }, [factor, loading, challengeMut]);

  if (loading) return <LoadingScreen />;
  if (!profile) return <Navigate to="/auth/sign-in" replace />;

  // Already at AAL2 — nothing to do here, send them on.
  if (mfa.currentLevel === 'aal2') {
    const dest = navState?.from?.pathname ?? '/dashboard';
    return <Navigate to={dest} replace />;
  }

  // No verified factor — they need the setup flow, not the challenge flow.
  if (!factor) {
    return <Navigate to="/auth/mfa/setup" state={{ from: navState?.from }} replace />;
  }

  const onResend = async () => {
    setIssuing(true);
    try {
      const res = await challengeMut.mutateAsync({ factorId: factor.id });
      setChallengeId(res.challengeId);
      setCode('');
      toast.success('Code resent.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not resend.';
      toast.error(msg);
    } finally {
      setIssuing(false);
    }
  };

  const onVerify = async (overrideCode?: string) => {
    if (!challengeId) {
      toast.error('No active challenge — please tap Resend.');
      return;
    }
    const finalCode = overrideCode ?? code;
    if (finalCode.length !== 6) return;
    try {
      await verifyMut.mutateAsync({ factorId: factor.id, challengeId, code: finalCode });
      toast.success('Verified.');
      const dest = navState?.from?.pathname ?? '/dashboard';
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Code rejected.';
      toast.error(msg);
      setCode('');
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background p-6">
      <div className="w-full max-w-md space-y-6">
        <Logo variant="symbol" tone="humus" className="h-10 w-10" />
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-2.5 py-1 text-xs font-medium text-muted-foreground">
            <ShieldCheck className="h-3 w-3 text-old-growth" />
            2-step verification
          </div>
          <h1 className="text-2xl font-medium tracking-headline leading-headline">
            Enter your verification code
          </h1>
          <p className="text-sm text-muted-foreground leading-body">
            We sent a 6-digit code to{' '}
            <span className="font-medium text-foreground">
              {factor.friendly_name ? maskPhone(factor.friendly_name) : 'your phone'}
            </span>
            . The code expires in 5 minutes.
          </p>
        </div>

        <div className="space-y-4 rounded-lg border border-border bg-card p-6">
          <div className="space-y-2">
            <Label>Code</Label>
            <OtpInput
              value={code}
              onChange={setCode}
              onComplete={(c) => onVerify(c)}
              disabled={verifyMut.isPending || issuing}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={onResend}
              disabled={issuing || verifyMut.isPending}
            >
              {issuing ? 'Sending…' : 'Resend'}
            </Button>
            <Button
              onClick={() => onVerify()}
              disabled={code.length !== 6 || verifyMut.isPending}
              className="ml-auto"
            >
              {verifyMut.isPending ? 'Verifying…' : 'Verify'}
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Lost your phone?{' '}
            <span className="text-foreground">
              Contact a Super Admin to remove your factor.
            </span>
          </span>
          <button
            type="button"
            onClick={() => signOut()}
            className="underline underline-offset-2 hover:text-foreground"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Mask all but the last 4 digits — `+15551234567` -> `•••-•••-4567`.
 * Hides the number from anyone glancing at the screen while still letting
 * the user confirm it's the right phone.
 */
function maskPhone(e164: string): string {
  const digits = e164.replace(/\D/g, '');
  if (digits.length < 4) return e164;
  const last4 = digits.slice(-4);
  return `•••-•••-${last4}`;
}
